import { useState, useEffect } from 'react';
import { fetchWithAuth } from 'shared/api/fetch-with-auth';
import { API_URL } from 'constants/index';
import { useSession } from '@clerk/clerk-react';

interface Building {
  id: string;
  owner: boolean;
  acquisitionPhase?: string;
  company?: { name: string };
}

interface Unit {
  id: string;
  buildingId: string;
  unitType?: string;
}

interface BuildingsAndUnitsData {
  buildings: Building[];
  units: Unit[];
}

interface UseBuildingsAndUnitsReturn {
  buildings: Building[];
  units: Unit[];
  getUnitsForBuilding: (buildingId: string) => Unit[];
  getBuildingFromUnit: (unitId: string) => Building | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

// Global cache to share data across all hook instances
let globalCache: BuildingsAndUnitsData | null = null;
let globalLoading = false;
let globalError: string | null = null;
const subscribers = new Set<() => void>();

export const useBuildingsAndUnits = (): UseBuildingsAndUnitsReturn => {
  const { session } = useSession();
  const [data, setData] = useState<BuildingsAndUnitsData>(globalCache || { buildings: [], units: [] });
  const [isLoading, setIsLoading] = useState(globalLoading);
  const [error, setError] = useState<string | null>(globalError);

  const notifySubscribers = () => {
    subscribers.forEach(callback => callback());
  };

  const fetchData = async () => {
    if (globalCache) return; // Already have data

    try {
      globalLoading = true;
      globalError = null;
      setIsLoading(true);
      setError(null);

      const response = await fetchWithAuth(
        `${API_URL}/building/simple`,
        { method: "GET" },
        session
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch buildings and units: ${response.status}`);
      }

      const responseData = await response.json();
      
      globalCache = responseData;
      globalLoading = false;
      
      setData(responseData);
      setIsLoading(false);
      notifySubscribers();
      
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch buildings and units';
      globalError = errorMessage;
      globalLoading = false;
      
      setError(errorMessage);
      setIsLoading(false);
      notifySubscribers();
    }
  };

  const refetch = async () => {
    globalCache = null; // Clear cache to force refetch
    await fetchData();
  };

  useEffect(() => {
    // Subscribe to global state changes
    const updateState = () => {
      if (globalCache) setData(globalCache);
      setIsLoading(globalLoading);
      setError(globalError);
    };

    subscribers.add(updateState);

    // Fetch data if not already available
    if (!globalCache && !globalLoading) {
      fetchData();
    } else if (globalCache) {
      setData(globalCache);
      setIsLoading(false);
      setError(null);
    }

    return () => {
      subscribers.delete(updateState);
    };
  }, [session]);

  // Utility functions for filtering data
  const getUnitsForBuilding = (buildingId: string): Unit[] => {
    return data.units.filter(unit => unit.buildingId === buildingId);
  };

  const getBuildingFromUnit = (unitId: string): Building | null => {
    const unit = data.units.find(u => u.id === unitId);
    if (!unit) return null;
    
    return data.buildings.find(b => b.id === unit.buildingId) || null;
  };

  return {
    buildings: data.buildings,
    units: data.units,
    getUnitsForBuilding,
    getBuildingFromUnit,
    isLoading,
    error,
    refetch
  };
}; 